import {Intercom} from '@intercom/messenger-js-sdk'
import {Box} from '@mui/material'
import type {FC, ReactElement} from 'react'
import {StrictMode, useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {Outlet, useLocation, useNavigate} from 'react-router-dom'
import {useUpdateTenantsCurrentUserMutation} from '@apis/infrastructureTenants'
import type {AuthContextProps} from '@containers/App/AuthProvider'
import {useAuthContext} from '@containers/App/AuthProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useUserContext} from '@containers/App/UserProvider'
import MBAIMainSideMenu from '@containers/Layout'
import Appbar from '@containers/Layout/appbar/Appbar'
import AgreeToTerms from '@shared/components/auth/AgreeToTerms'
import ErrorBoundary from '@shared/components/ErrorBoundary'
import AgentChat from '@containers/Dashboards/AgentChat'
import {useAlpinistUrlSync} from '@shared/hooks/useAlpinistUrlSync'
import {useInvestigateFromUrl} from '@shared/hooks/useInvestigateFromUrl'
import {useOrganizationAccess} from '@shared/hooks/useOrganizationAccess'
import {useProactiveReauthorize} from '@shared/hooks/useProactiveReauthorize'
import {PageContextProvider} from '@shared/alpinist/pageContext'

const {VITE_INTERCOM_APP_ID} = import.meta.env

const RIGHT_MIN = 300
const RIGHT_MAX = 1600
const DEFAULT_RIGHT = RIGHT_MIN
const WIDTH_KEY = 'assistantWidth'

type Props = {
  isMobile: boolean
}

const DashTemplate: FC<Props> = ({isMobile = false}): ReactElement => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const handleDrawerOpen = useCallback(() => setDrawerOpen((v) => !v), [])
  const [updateUser] = useUpdateTenantsCurrentUserMutation()

  const {logoutUser, loginUser} = useAuthContext() as AuthContextProps
  const {currentUser, welcomeSkipped} = useUserContext() as UserContextProps
  // Only ACTIVE tenants count as "has an organization". Without one, a user
  // lands on /no-organization (AB#6851): since AB#6814 an unknown-domain sign-up
  // gets no tenant at all, and organizations are registered only by staff
  // (AB#6862), so there is no sign-up to complete.
  const {activeOrganizations: tenantsData, access: organizationAccess} =
    useOrganizationAccess(!currentUser)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const {
    isOpen: assistantOpen,
    toggle: toggleAssistant,
    conversationId,
    onConversationChange,
  } = useAlpinistUrlSync()
  // Alert links (email, Teams) land here with ?investigate=<violation id>.
  useInvestigateFromUrl()
  // A tab left open past its token's expiry re-authorizes on load and on the
  // next in-app navigation (AB#6617, AB#6757). Here, in the signed-in shell, so
  // public routes and the sign-in pages never run it.
  useProactiveReauthorize()
  const isMobileAssistantRoute = isMobile && location.pathname.startsWith('/agent-chat')
  const assistantOpenForUi = isMobile ? isMobileAssistantRoute : assistantOpen
  const lastMobileNonAssistantRouteRef = useRef('/cockpit')

  const [assistantFullWidth, setAssistantFullWidth] = useState(false)
  const [assistantDrafts, setAssistantDrafts] = useState<Record<string, string>>({})
  const toggleAssistantFullWidth = useCallback(() => {
    setAssistantFullWidth((v) => !v)
  }, [])
  const [assistantWidth, setAssistantWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(WIDTH_KEY))
    return Number.isFinite(saved) && saved >= RIGHT_MIN && saved <= RIGHT_MAX
      ? saved
      : DEFAULT_RIGHT
  })

  useEffect(() => {
    if (currentUser?.userprofile && !currentUser.userprofile.preferred_timezone) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      updateUser({body: {preferred_timezone: timezone}})
    }
  }, [currentUser, updateUser])

  const assistantDraft = conversationId ? (assistantDrafts[conversationId] ?? '') : ''
  const setAssistantDraft = useCallback(
    (value: string) => {
      if (!conversationId) {
        return
      }

      setAssistantDrafts((prev) => {
        if (!value) {
          if (!(conversationId in prev)) {
            return prev
          }

          const next = {...prev}
          delete next[conversationId]
          return next
        }

        if (prev[conversationId] === value) {
          return prev
        }

        return {...prev, [conversationId]: value}
      })
    },
    [conversationId],
  )

  const rowRef = useRef<HTMLDivElement | null>(null)
  const draggingRef = useRef(false)

  // drag
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    draggingRef.current = true
    document.body.style.cursor = 'col-resize'
  }, [])

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingRef.current || !rowRef.current) {
      return
    }
    const rect = rowRef.current.getBoundingClientRect()
    const fromRight = rect.right - e.clientX
    const next = Math.min(Math.max(fromRight, RIGHT_MIN), RIGHT_MAX)
    setAssistantWidth(next)
  }, [])

  const onMouseUp = useCallback(() => {
    if (!draggingRef.current) {
      return
    }
    draggingRef.current = false
    document.body.style.cursor = ''
    localStorage.setItem(WIDTH_KEY, String(assistantWidth))
  }, [assistantWidth])

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [onMouseMove, onMouseUp])

  // legacy auth
  const oldAuth = localStorage.getItem('auth')
  useEffect(() => {
    if (oldAuth) {
      const creds = JSON.parse(oldAuth)
      localStorage.removeItem('auth')
      loginUser(creds)
    }
  }, [oldAuth, dispatch, loginUser])

  const tenantsFetchedRef = useRef(false)
  useEffect(() => {
    if (currentUser?.userprofile && !currentUser.userprofile.roles) {
      logoutUser()
    }
    if (currentUser && organizationAccess && !tenantsFetchedRef.current) {
      tenantsFetchedRef.current = true
      if (organizationAccess === 'none') {
        navigate('/no-organization')
      }
    }
  }, [currentUser, logoutUser, navigate, organizationAccess])

  useEffect(() => {
    if (
      location.pathname === '/login' ||
      location.pathname === '/create-account' ||
      location.pathname.includes('authentication')
    ) {
      navigate('/')
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    // Don't prompt for profile preferences until the user has an ACTIVE
    // tenant — otherwise this fires while they're still on /no-organization,
    // and they'd be prompted again once they join.
    const hasActiveTenant = (tenantsData?.count ?? 0) > 0
    if (
      hasActiveTenant &&
      currentUser?.userprofile?.onboarding_tutorial_last_step_completed === -2 &&
      !welcomeSkipped
    ) {
      navigate('/welcome')
    }
  }, [currentUser, welcomeSkipped, navigate, tenantsData])

  useEffect(() => {
    if (!isMobile) {
      return
    }

    setDrawerOpen(false)

    if (location.pathname.startsWith('/agent-chat')) {
      return
    }

    lastMobileNonAssistantRouteRef.current = `${location.pathname}${location.search}`

    if (assistantOpen) {
      toggleAssistant()
    }
  }, [isMobile, location.pathname, location.search, assistantOpen, toggleAssistant])

  const handleAssistantAction = useCallback(() => {
    if (isMobile) {
      if (isMobileAssistantRoute) {
        navigate(lastMobileNonAssistantRouteRef.current)
        return
      }
      navigate('/agent-chat')
      return
    }
    toggleAssistant()
  }, [isMobile, isMobileAssistantRoute, navigate, toggleAssistant])

  useEffect(() => {
    if (currentUser?.userprofile?.intercom_hmac && VITE_INTERCOM_APP_ID) {
      Intercom({
        app_id: VITE_INTERCOM_APP_ID,
        user_id: currentUser.id.toString(),
        user_hash: currentUser.userprofile.intercom_hmac,
        name: `${currentUser.first_name} ${currentUser.last_name}`,
        email: currentUser.email,
        company: {
          name: currentUser.userprofile.company,
          company_id: currentUser.userprofile.current_tenant,
        },
        language_override: currentUser.userprofile.preferred_language || undefined,
        hide_default_launcher: true,
      })
    }
  }, [currentUser])

  return (
    <StrictMode>
      {currentUser?.userprofile && !currentUser.userprofile.has_agreed_to_terms_and_conditions ? (
        <AgreeToTerms />
      ) : null}

      <Box
        component="main"
        data-testid="mbai-main-dash-template-component"
        sx={{display: 'flex', height: '100dvh', overflow: 'hidden'}}
      >
        <MBAIMainSideMenu open={drawerOpen} onMenuOpen={handleDrawerOpen} isMobile={isMobile} />

        {/* Column: Appbar at top, row with content below */}
        <Box sx={{flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
          {/* Appbar spans full width, untouched by assistant */}
          <Appbar
            onToggleDrawer={handleDrawerOpen}
            onToggleAssistant={handleAssistantAction}
            assistantOpen={assistantOpenForUi}
            isMobile={isMobile}
          />

          {/* Row below the Appbar */}
          <Box
            ref={rowRef}
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            {/* Main content area */}
            <Box
              sx={{
                flex: '1 1 0%', // let it grow and shrink
                minWidth: 0, // critical so it can shrink
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ErrorBoundary>
                {!isMobile && assistantFullWidth && assistantOpen ? (
                  <AgentChat
                    fullWidth={assistantFullWidth}
                    onToggleFullWidth={toggleAssistantFullWidth}
                    initialConversationId={conversationId}
                    onConversationChange={onConversationChange}
                    draft={assistantDraft}
                    onDraftChange={setAssistantDraft}
                  />
                ) : (
                  <Outlet />
                )}
              </ErrorBoundary>
            </Box>

            {/* Resizer + Assistant (right pane) ONLY on desktop/tablet */}
            {!isMobile && !assistantFullWidth && assistantOpen && (
              <>
                <Box
                  onMouseDown={onMouseDown}
                  sx={{
                    width: 5,
                    flex: '0 0 auto',
                    cursor: 'col-resize',
                    userSelect: 'none',
                    background:
                      'linear-gradient(to right, transparent, rgba(0,0,0,0.08), transparent)',
                    '&:hover': {
                      background:
                        'linear-gradient(to right, transparent, rgba(0,0,0,0.16), transparent)',
                    },
                  }}
                />
                {/* In look_at_screen's image, but not in its text (AB#6762). */}
                <Box
                  data-alpinist-panel
                  sx={{
                    flex: '0 0 auto',
                    width: assistantWidth,
                    minWidth: RIGHT_MIN,
                    maxWidth: RIGHT_MAX,
                    borderLeft: (t) => `1px solid ${t.palette.divider}`,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <AgentChat
                    fullWidth={assistantFullWidth}
                    onToggleFullWidth={toggleAssistantFullWidth}
                    initialConversationId={conversationId}
                    onConversationChange={onConversationChange}
                    draft={assistantDraft}
                    onDraftChange={setAssistantDraft}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </StrictMode>
  )
}

// What the user has open, for the Alpinist side panel (AB#6762): pages publish
// into it, the panel reads it when a message is sent.
const DashTemplateWithPageContext: FC<Props> = (props): ReactElement => (
  <PageContextProvider>
    <DashTemplate {...props} />
  </PageContextProvider>
)

export default DashTemplateWithPageContext
